import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useFY, getFYOptions } from '../context/FYContext';
import { getNotifications, markAllNotificationsRead } from '../lib/api';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { usePageShortcuts } from '../context/PageShortcutContext';
import { ShortcutsHelp } from './ShortcutsHelp';
import { ShortcutHints } from './ShortcutHints';
import { QuickOpen } from './QuickOpen';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from './ui/dropdown-menu';
import { Badge } from './ui/badge';
import {
  Home,
  Bell,
  Moon,
  Sun,
  LogOut,
  ChevronDown,
  User,
  Keyboard,
  Search,
  Calendar
} from 'lucide-react';

const PAGE_TITLES = {
  '/dashboard': 'Dashboard',
  '/income': 'Income',
  '/expense': 'Expense',
  '/sales': 'Sales',
  '/ledger': 'Ledger',
  '/recurring': 'Recurring',
  '/audit-trail': 'Audit Trail',
  '/deleted-records': 'Deleted Records',
  '/users': 'Users',
  '/all-entries': 'All Entries',
};

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { selectedFY, setSelectedFY, shortLabel, currentFYStart } = useFY();
  const fyOptions = getFYOptions(currentFYStart);
  const location = useLocation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showQuickOpen, setShowQuickOpen] = useState(false);
  const { getCallbacks } = usePageShortcuts();

  // Keyboard shortcuts
  useKeyboardShortcuts({
    onNewEntry: () => getCallbacks().onNewEntry?.(),
    onPrevTab: () => getCallbacks().onPrevTab?.(),
    onNextTab: () => getCallbacks().onNextTab?.(),
    onDateQuick: (key) => getCallbacks().onDateQuick?.(key),
    onDateCustom: () => getCallbacks().onDateCustom?.(),
    onClearAction: () => getCallbacks().onClearAction?.(),
    onShowHelp: () => setShowShortcuts(true),
  });

  // Enter key form navigation is handled via EnterForm wrapper in useFormEnterNav.js

  // Quick Open shortcut: '/' key
  useEffect(() => {
    const handleSlash = (e) => {
      const tag = e.target.tagName.toLowerCase();
      const isInput = tag === 'input' || tag === 'textarea' || tag === 'select' || e.target.isContentEditable;
      if (isInput) return;
      if (e.key === '/' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        setShowQuickOpen(true);
      }
    };
    window.addEventListener('keydown', handleSlash);
    return () => window.removeEventListener('keydown', handleSlash);
  }, []);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const res = await getNotifications();
        setNotifications(res.data);
      } catch (error) {
        console.error('Failed to fetch notifications');
      }
    };
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const unreadCount = notifications.filter(n => !n.read).length;
  const pageTitle = PAGE_TITLES[location.pathname] || '';

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Failed to mark notifications as read');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Top Navigation Bar */}
      <header className="sticky top-0 z-40 h-14 flex items-center justify-between px-4 md:px-6 border-b border-border/60 bg-background/90 backdrop-blur-md">
        {/* Left: Home + Page Title */}
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-xl"
            onClick={() => navigate('/')}
            data-testid="home-button"
          >
            <Home className="h-[18px] w-[18px]" />
          </Button>
          {pageTitle && (
            <>
              <div className="h-4 w-px bg-border" />
              <span className="text-sm font-semibold tracking-tight">{pageTitle}</span>
            </>
          )}
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-0.5 sm:gap-1.5">
          {/* FY Selector */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 sm:h-9 rounded-xl px-2 sm:px-3 text-xs sm:text-sm font-medium gap-1" data-testid="fy-selector">
                <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span>{shortLabel}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-xl min-w-[180px]">
              <div className="px-3 py-1.5 border-b">
                <span className="text-xs font-semibold text-muted-foreground">Financial Year</span>
              </div>
              {fyOptions.map(fy => (
                <DropdownMenuItem
                  key={fy.value}
                  onClick={() => setSelectedFY(fy.value)}
                  className={selectedFY === fy.value ? 'bg-primary/10 text-primary font-medium' : ''}
                  data-testid={`fy-option-${fy.value}`}
                >
                  <div className="flex flex-col">
                    <span className="text-sm">{fy.label}</span>
                    <span className="text-xs text-muted-foreground">{fy.range}</span>
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Quick Open */}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 sm:h-9 sm:w-9 rounded-xl"
            onClick={() => setShowQuickOpen(true)}
            data-testid="quick-open-button"
            title="Quick Open (/)"
          >
            <Search className="h-4 w-4 sm:h-[18px] sm:w-[18px]" />
          </Button>

          {/* Shortcuts hint */}
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-xl hidden sm:flex"
            onClick={() => setShowShortcuts(true)}
            data-testid="shortcuts-button"
          >
            <Keyboard className="h-[18px] w-[18px]" />
          </Button>

          {/* Theme Toggle */}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 sm:h-9 sm:w-9 rounded-xl"
            onClick={toggleTheme}
            data-testid="theme-toggle"
          >
            {theme === 'dark' ? <Sun className="h-4 w-4 sm:h-[18px] sm:w-[18px]" /> : <Moon className="h-4 w-4 sm:h-[18px] sm:w-[18px]" />}
          </Button>

          {/* Notifications */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="relative h-8 w-8 sm:h-9 sm:w-9 rounded-xl" data-testid="notifications-button">
                <Bell className="h-4 w-4 sm:h-[18px] sm:w-[18px]" />
                {unreadCount > 0 && (
                  <Badge
                    variant="destructive"
                    className="absolute -top-0.5 -right-0.5 h-4 w-4 p-0 flex items-center justify-center text-[10px]"
                  >
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80 rounded-xl">
              <div className="flex items-center justify-between px-3 py-2 border-b">
                <span className="font-semibold text-sm">Notifications</span>
                {unreadCount > 0 && (
                  <Button variant="ghost" size="sm" onClick={handleMarkAllRead} className="text-xs h-7">
                    Mark all read
                  </Button>
                )}
              </div>
              <ScrollArea className="max-h-72">
                {notifications.length === 0 ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    No notifications
                  </div>
                ) : (
                  notifications.slice(0, 10).map(notification => (
                    <DropdownMenuItem key={notification.id} className="flex flex-col items-start p-3 cursor-default">
                      <span className={`text-sm ${notification.read ? 'text-muted-foreground' : 'font-medium'}`}>
                        {notification.message}
                      </span>
                      <span className="text-xs text-muted-foreground mt-1">
                        {new Date(notification.created_at).toLocaleDateString()}
                      </span>
                    </DropdownMenuItem>
                  ))
                )}
              </ScrollArea>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-1 sm:gap-2 h-8 sm:h-9 rounded-xl px-1.5 sm:px-2" data-testid="user-menu">
                <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-primary flex items-center justify-center">
                  <User className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-primary-foreground" />
                </div>
                <span className="hidden sm:inline text-sm font-medium">{user?.name}</span>
                <ChevronDown className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-xl">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-muted-foreground">@{user?.username}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-button">
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Page Content */}
      <main className={location.pathname === '/' ? '' : 'max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 pb-6 sm:pb-[120px]'}>
        {children}
      </main>

      {/* Shortcuts Help Dialog */}
      <ShortcutsHelp open={showShortcuts} onClose={() => setShowShortcuts(false)} />

      {/* Quick Open */}
      <QuickOpen open={showQuickOpen} onClose={() => setShowQuickOpen(false)} />

      {/* Shortcut Hints Bar */}
      <ShortcutHints />
    </div>
  );
};
